import { useQueryClient } from '@tanstack/react-query'
import { includes } from 'lodash'
import { useCallback, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import useGetPartnerConfigByOrgId from '~/features/partners/hooks/useGetPartnerConfigByOrgId'
import useIsPartner from '~/features/partners/hooks/useIsPartner'
import { EPartnerDomains } from '~/features/partners/models'
import {
  isLibbyDomain,
  queries as partnerQueries,
} from '~/features/partners/utils'
import { EClientPath, EMarketingClientPath } from '~/models/api'
import AuthService from '~/services/auth'
import { store } from '~/store'

const useSignOut = () => {
  const [isLoading, setIsLoading] = useState(false)

  const orgId = store((state) => state.user.organizationId)
  const { data: partnerData } = useGetPartnerConfigByOrgId(orgId)
  const queryClient = useQueryClient()

  const navigate = useNavigate()
  const { pathname } = useLocation()

  const isPartner = useIsPartner() || !isLibbyDomain()

  const isMarketingPath = includes(
    Object.values(EMarketingClientPath),
    pathname,
  )

  const signOut = useCallback(async () => {
    try {
      setIsLoading(true)
      await AuthService.signOut()

      let partnerDomain: string = partnerData?.data?.domain || ''

      if (!partnerDomain) {
        try {
          partnerDomain =
            (
              await queryClient.fetchQuery({
                ...partnerQueries.partnerByDomain(true),
                staleTime: 0,
              })
            )?.data?.domain || ''
        } catch (e) {
          // do nothing.
        }
      }

      if (
        [EPartnerDomains.virtualGurus, EPartnerDomains.whiteLabelChris].some(
          (link) => partnerDomain.includes(link),
        )
      ) {
        window.location.href = import.meta.env
          .VITE_VIRTUAL_GURUS_OIDC_END_SESSION_URL as string
      } else if (!isMarketingPath) {
        navigate(isPartner ? EClientPath.partnerAuth : EClientPath.login)
      }
    } finally {
      setIsLoading(false)
    }
  }, [
    isMarketingPath,
    isPartner,
    navigate,
    partnerData?.data?.domain,
    queryClient,
  ])

  return { isLoading, signOut }
}

export default useSignOut
